"use client";

import React from "react";
import {
  Calendar,
  Clock,
  User,
  ArrowLeft,
  Share2,
  FolderOpen,
  ArrowRight
} from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="space-y-6 animate-pulse">
          <div className="h-6 w-32 rounded-md bg-muted" />
          <div className="h-12 w-3/4 rounded-md bg-muted" />
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-full bg-muted" />
            <div className="space-y-2">
              <div className="h-4 w-28 rounded bg-muted" />
              <div className="h-3 w-20 rounded bg-muted" />
            </div>
          </div>
          <div className="h-96 w-full rounded-2xl bg-muted" />
          <div className="space-y-4 pt-6">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-5/6 rounded bg-muted" />
            <div className="h-4 w-4/6 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <p className="text-muted-foreground mb-4">Failed to load the article.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const currentPost = posts && posts.length > 0 ? posts[0] : null;
  const relatedPosts = posts && posts.length > 1 ? posts.slice(1, 4) : [];

  if (!currentPost) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
        <h2 className="text-2xl font-semibold font-serif text-foreground mb-3">
          Post not found
        </h2>
        <p className="text-muted-foreground mb-6">
          The renovation article you are looking for is unavailable or has moved.
        </p>
        <Link to="/BlogPage">
          <Button variant="default">Return to articles</Button>
        </Link>
      </div>
    );
  }

  const formattedDate = currentPost.date
    ? new Date(currentPost.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Recently published";

  const authorName = currentPost.author?.name || "Decatur Heritage Team";
  const authorAvatar =
    currentPost.author?.avatar?.["96"] ||
    currentPost.author?.avatar?.["48"] ||
    "";
  const categoryName =
    currentPost.categories && currentPost.categories.length > 0
      ? currentPost.categories[0].name
      : "Home Remodeling";

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      {/* Back to Blog Navigation */}
      <div className="mb-8">
        <Link
          to="/BlogPage"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to all articles
        </Link>
      </div>

      {/* Header Info */}
      <header className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            <FolderOpen className="size-3.5" />
            {categoryName}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="size-3.5" />
            {formattedDate}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="size-3.5" />
            5 min read
          </span>
        </div>

        <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
          {currentPost.title}
        </h1>

        {/* Author Byline Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-y border-border">
          <div className="flex items-center gap-3.5">
            <Avatar className="size-11 border border-border">
              {authorAvatar ? (
                <AvatarImage src={authorAvatar} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-muted text-foreground font-medium">
                {authorName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {authorName}
              </div>
              <div className="text-xs text-muted-foreground">
                Decatur Heritage Renovations
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                if (typeof navigator !== "undefined" && navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
            >
              <Share2 className="size-3.5 mr-1" />
              Share article
            </Button>
          </div>
        </div>
      </header>

      {/* Featured Image Display Area */}
      {currentPost.featuredImage ? (
        <div className="my-10 overflow-hidden rounded-2xl border border-border shadow-sm">
          <img
            src={currentPost.featuredImage}
            alt={currentPost.title}
            className="w-full object-cover max-h-[500px]"
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : (
        <div className="my-10 h-72 w-full overflow-hidden rounded-2xl border border-border bg-muted/40 flex items-center justify-center text-muted-foreground text-sm">
          Decatur Heritage Renovations Project Gallery
        </div>
      )}

      {/* Post Main Content */}
      <div className="mt-8 space-y-6 text-foreground leading-relaxed text-base sm:text-lg">
        {currentPost.content ? (
          <div
            className="prose prose-neutral max-w-none space-y-4"
            dangerouslySetInnerHTML={{ __html: currentPost.content }}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-lg leading-relaxed text-foreground">
              {currentPost.excerpt ||
                "Every renovation in Decatur carries a unique story of architectural character, historic craftsmanship, and tailored modern living. Our team approaches each project with meticulous planning, from kitchen upgrades to whole-home master suites."}
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Working with homes across Oakhurst, Winnona Park, and Great Lakes requires an appreciation for classic layouts alongside high-efficiency appliances and durable finishes. Whether restoring historic molding or opening up closed-off floorplans, careful trade sequencing guarantees a smooth build.
            </p>
          </div>
        )}
      </div>

      {/* Author Bio Box */}
      <div className="my-14 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <Avatar className="size-16 border border-border">
            {authorAvatar ? (
              <AvatarImage src={authorAvatar} alt={authorName} />
            ) : null}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-lg font-semibold">
              {authorName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                Written by
              </span>
            </div>
            <h3 className="text-lg font-bold text-card-foreground">
              {authorName}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Craftsman and project specialist at Decatur Heritage Renovations, dedicated to residential remodeling, historic carpentry, and custom room additions throughout Decatur, Georgia.
            </p>
          </div>
        </div>
      </div>

      <Separator className="my-12" />

      {/* Related Posts Section */}
      {relatedPosts.length > 0 && (
        <section aria-labelledby="related-posts-heading" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2
              id="related-posts-heading"
              className="font-serif text-2xl font-bold text-foreground"
            >
              Related articles
            </h2>
            <Link
              to="/BlogPage"
              className="inline-flex items-center text-sm font-medium text-foreground hover:underline"
            >
              View all
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((post, i) => (
              <Card
                key={post.id}
                data-index={i}
                className="overflow-hidden border border-border bg-card transition-shadow hover:shadow-md flex flex-col justify-between"
              >
                {post.featuredImage && (
                  <div className="h-44 w-full overflow-hidden bg-muted">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <CardHeader className="space-y-2 p-5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {post.date
                      ? new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Recent"}
                  </span>
                  <CardTitle className="font-serif text-lg font-semibold line-clamp-2 text-card-foreground leading-snug">
                    {post.title}
                  </CardTitle>
                  {post.excerpt && (
                    <CardDescription className="line-clamp-2 text-sm text-muted-foreground">
                      {post.excerpt.replace(/<[^>]+>/g, "")}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="p-5 pt-0">
                  {post.link ? (
                    <Link to={post.link}>
                      <Button variant="outline" size="sm" className="w-full text-xs">
                        Read story
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/BlogPage">
                      <Button variant="outline" size="sm" className="w-full text-xs">
                        Read story
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Consultation Callout Footer */}
      <div className="mt-16 rounded-2xl bg-secondary p-8 text-center sm:p-10 border border-border">
        <h3 className="font-serif text-2xl font-bold text-secondary-foreground mb-2">
          Planning a renovation in Decatur?
        </h3>
        <p className="mx-auto max-w-xl text-sm sm:text-base text-muted-foreground mb-6">
          Schedule a consultation with our local design and build team to discuss your kitchen, bath, or full-home project.
        </p>
        <Link to="/ContactPage">
          <Button variant="default" size="lg">
            Start a consultation
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function SinglePost() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", per_page: 4 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="relative w-full bg-background text-foreground"
    >
      <PostsProvider wp_query={wp_query} className="w-full">
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}